import styles from './Alert.module.css';
import type { AlertProps, AlertType } from './Alert.types';
import { IconType } from '@/_types/icons';
import { Button, ButtonGroup, Text, Icon, Flex } from '@/_components';
import { getClasses } from '@/_utils/classnames';
import type { ColorType } from '@/_types/colors';
import { type FC } from 'react';

const TEXT_COLOR_MAP: Record<AlertType, ColorType> = {
  danger: 'critical',
  info: 'accent',
  note: 'primary',
  success: 'success',
  warning: 'caution',
};

export const Alert: FC<AlertProps> = ({
  children,
  className,
  type,
  onDismiss,
  isLimited,
  isLimitedPadding,
  title,
  icon,
  actions,
  isFlat,
}) => {
  const alertClasses: string = getClasses(styles.alert, className, {
    [styles.alertInfo]: type === 'info',
    [styles.alertDanger]: type === 'danger',
    [styles.alertWarning]: type === 'warning',
    [styles.alertSuccess]: type === 'success',
    [styles.alertNote]: type === 'note',
    [styles.alertFlat]: isFlat,
    [styles.alertWithDismiss]: !!onDismiss,
    [styles.alertLimitedHeight]: isLimited,
    [styles.alertLimitedPadding]: isLimitedPadding,
  });

  const getIcon = (): IconType | null => {
    if (!icon || icon === null) return null;
    if (icon) return icon;

    if (type === 'success') return null;

    return 'warning';
  };
  const iconName = getIcon();

  return (
    <Flex
      className={alertClasses}
      data-component="sc-alert"
      isBetween={true}
      padding={6}
      isFullWidth={isFlat}
      gap={6}
      isColumn={true}
    >
      <Flex gap={4} isFullWidth={isFlat}>
        {iconName && !title && (
          <Icon size={16} name={iconName} color={TEXT_COLOR_MAP[type]} />
        )}
        <Flex isColumn={true} gap={2} isFullWidth={isFlat}>
          {!!title && (
            <Flex gap={4} isYCentered={true}>
              {iconName && title && (
                <Icon size={16} name={iconName} color={TEXT_COLOR_MAP[type]} />
              )}
              {title && (
                <Text
                  type="small"
                  fontWeight="bold"
                  color={TEXT_COLOR_MAP[type]}
                >
                  {title}
                </Text>
              )}
            </Flex>
          )}
          <Text
            type="small"
            color={TEXT_COLOR_MAP[type]}
            lineClamp={isLimited ? 2 : undefined}
          >
            {children}
          </Text>
          {onDismiss && (
            <ButtonGroup className={styles.alert__dismiss}>
              <Button
                intent="secondary"
                iconLeft="x"
                tooltip="Dismiss"
                withTooltip
                tooltipPlacement="bottom"
                value="Dismiss"
                isIconOnly={true}
                onClick={onDismiss}
                size="tiny"
              />
            </ButtonGroup>
          )}
        </Flex>
      </Flex>
      {actions}
    </Flex>
  );
};
